import * as React from 'react'
import type { NavigateOptions } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { AttendanceShiftLocationTypeEnumValues } from 'generated/resources/datatable.graphql'
import {
  AttendanceEnumsShiftSourceEnum,
  AttendanceOpenShift,
  AttendanceBreakSetting as BreakSetting,
  ClockInWidgetEmployeesSettingFragment,
  ClockInWidgetPoliciesFragment,
  TimesheetBreakDurationRuleFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetPageLastWorkingShiftFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import { TimesheetOvertimeRequestFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { TrackingProperties, track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import { ProjectClockInData } from 'modules/attendance/components/ClockIn/Tracker/tracker'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import BreakSettingsUtils from 'modules/attendance/lib/utils/BreakSettings'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import OpenShiftUtils from 'modules/attendance/lib/utils/OpenShiftUtils'
import AttendanceOvertimeUtils from 'modules/attendance/lib/utils/OvertimeRequest'
import validateBreakDurationRules, {
  ValidationError,
  isBreakSetting,
} from 'modules/attendance/lib/validateShiftBreakDurationRules'
import type { ActionType } from 'modules/attendance/store/models/Shift'
import { ActionFrom, ActionTypes, LOCATION_TYPES } from 'modules/attendance/store/models/Shift'

import EmployeePulseQuestion from '../Tracker/Clockin/EmployeePulseQuestion'
import ClockOutReminderModal, { CheckClockOutProps } from './ClockOutReminderModal'
import FinishAutoBreakModal from './FinishAutoBreakModal'
import withHooks from './withHooks'

export interface RenderProps {
  loading: boolean
  clockedIn: boolean
  breakStarted: boolean
  hasAutomaticBreaks: boolean
  hasRestTime: boolean
  lastShiftLocationType?: string
  trackedDurationToday: number
  breakType: AttendanceBreakConfigurations | undefined
  openShift?: AttendanceOpenShift | null
  openShiftDuration: number
  onToggle: (
    action: ActionType,
    locationType?: string,
    attendanceBreak?: AttendanceBreakConfigurations | undefined,
    projectData?: ProjectClockInData
  ) => void
  today: moment.Moment
  employeeMissingClockOut: boolean
  projectClockInData: ProjectClockInData
  exceedsEstimatedThreshold: (shiftDuration?: number) => boolean
  restTimeAlertInconsistencies: ValidationError[]
  todayShifts: TimesheetPageShiftFragment[]
  maxMinutesPerDay: number
  maxRegularMinutes: number
  hasProjectEnabled: boolean
  lastWorkingShift?: TimesheetPageLastWorkingShiftFragment | null
  breaksEnabled: boolean
  desktopClockIn: boolean
  mobileClockIn: boolean
  canSeeOwnedAttendance: boolean
  breakConfigurations: AttendanceBreakConfigurations[]
  breakTypes: {
    id: string
    name: string
    isPaid: boolean
  }[]
  enableClockInLocation: boolean
  breakSetting: BreakSetting | null
}

interface Props {
  minute: number // Used to refresh the view;
  today: moment.Moment
  setToday: (date: string | null) => void
  lastWorkingShift?: TimesheetPageLastWorkingShiftFragment | null
  openShift?: AttendanceOpenShift | null
  onRefresh: () => void
  loading: boolean
  setLoading: (loading: boolean) => void
  breakSetting: BreakSetting | null
  requestOvertimeUrl: (hoursAmount: number) => string
  employeeTimeSheetUrl: string
  onChange?: () => void
  navigate: (route: string, options?: NavigateOptions) => void
  attendanceSetting: ClockInWidgetEmployeesSettingFragment
  overtimeRequest: TimesheetOvertimeRequestFragment | undefined
  shiftActions: ReturnType<typeof useShiftActions>
  hasProjectEnabled: boolean
  breakDurationRules: TimesheetBreakDurationRuleFragment[]
  todayShifts: TimesheetPageShiftFragment[]
  estimatedTime: TimesheetEstimatedTimeFragment
  policies?: ClockInWidgetPoliciesFragment
  breaksEnabled: boolean
  desktopClockIn: boolean
  mobileClockIn: boolean
  canSeeOwnedAttendance: boolean
  breakConfigurations: AttendanceBreakConfigurations[]
  breakType?: {
    id: string
    name: string
    isPaid: boolean
  }
  breakTypes: {
    id: string
    name: string
    isPaid: boolean
  }[]
}

interface State {
  showStillBreakModal: boolean
  showClockOutModal: boolean
  showMinBreakModal: boolean
  showFinishAutoBreakModal: boolean
  workedMinutes: number
  estimatedClockOutTime: moment.Moment
  showEmployeePulseQuestionModal: boolean
}

interface ToggleProps {
  action: ActionType
  estimatedClockOutTime?: moment.Moment
  from?: string
  locationType?: string
  attendanceBreak?: AttendanceBreakConfigurations | undefined
  projectData?: ProjectClockInData
}

interface ShiftToggleProps {
  now: string
  source?: AttendanceEnumsShiftSourceEnum
  date: string
  locationType?: string
  timeSettingsBreakConfigurationId?: number
  projectWorkerId?: number | null
  subprojectId?: number | null
}

class ClockInChecker extends React.Component<Props & React.ConsumerProps<RenderProps>, State> {
  state: State = {
    showStillBreakModal: false,
    showClockOutModal: false,
    showMinBreakModal: false,
    showFinishAutoBreakModal: false,
    showEmployeePulseQuestionModal: false,
    workedMinutes: 0,
    estimatedClockOutTime: moment(),
  }

  get openShiftDuration(): number {
    const { openShift } = this.props

    return OpenShiftUtils.duration(openShift)
  }

  get breakType(): AttendanceBreakConfigurations | undefined {
    const { openShift } = this.props

    if (openShift && openShift.timeSettingsBreakConfiguration) {
      const { name, id, paid } = openShift.timeSettingsBreakConfiguration

      return {
        name,
        timeSettingsBreakConfigurationId: id,
        paid,
      }
    }
  }

  get trackedMinutesToday(): number {
    return (
      AttendanceShiftUtils.trackedMinutes(this.props.todayShifts) +
      AttendanceShiftUtils.trackedPaidBreakMinutes(this.props.todayShifts)
    )
  }

  get trackedMinutesTodayWithOpenShift(): number {
    return this.trackedMinutesToday + this.openShiftDuration
  }

  get lastShiftLocationType(): string | undefined {
    const { lastWorkingShift, attendanceSetting } = this.props
    const { enableClockInLocation } = attendanceSetting || {}

    if (enableClockInLocation) return lastWorkingShift?.locationType || LOCATION_TYPES.OFFICE
  }

  projectClockInData(): ProjectClockInData {
    const { lastWorkingShift } = this.props
    const { projectManagementTimeRecords } = lastWorkingShift || {}
    const timeRecords = projectManagementTimeRecords || []
    const currentDate = dateToString(new Date(), 'backend')

    let projectWorkerId, subprojectId

    if (lastWorkingShift?.referenceDate === currentDate) {
      projectWorkerId = timeRecords[0]?.projectWorker?.id
      subprojectId = timeRecords[0]?.subproject?.id
    }

    return { projectWorkerId, subprojectId }
  }

  breakTimeRequired(workedMinutes: number): number {
    const { breakSetting } = this.props

    if (!breakSetting) return 0

    return BreakSettingsUtils.breakTimeRequired(breakSetting, workedMinutes)
  }

  checkClockOutToggle = ({
    estimatedClockOutTime = moment(),
    from = ActionFrom.TIME_TRACKING_WIDGET,
    forgotClockOut = false,
  }: CheckClockOutProps) => {
    const checker = this.workedMinutesCheckShowClockOutModal

    if (checker()) return

    this.confirmClockOut(estimatedClockOutTime, from, forgotClockOut)
  }

  exceedsWithDuration(duration: number): boolean {
    const trackedMinutes = this.trackedMinutesToday
    const estimatedMinutes = this.props.estimatedTime.minutes
    return trackedMinutes <= estimatedMinutes && trackedMinutes + duration > estimatedMinutes
  }

  workedMinutesCheckShowClockOutModal = (): boolean => {
    const { attendanceSetting, policies } = this.props
    const canSeeAttendance = !!policies?.seeAttendance?.can

    const clockOutReminderEnabled =
      EmployeesSettingsUtils.clockOutReminderEnabled(attendanceSetting)
    const missingClockOutEnabled = EmployeesSettingsUtils.missingClockOutEnabled(attendanceSetting)
    const { showClockOutModal, showStillBreakModal } = this.state

    const enableReminder =
      clockOutReminderEnabled || (hasFeature('clockout_reminder') && !missingClockOutEnabled)

    if (
      !showClockOutModal &&
      !showStillBreakModal &&
      canSeeAttendance &&
      enableReminder &&
      this.exceedsWithDuration(this.openShiftDuration)
    ) {
      this.setState({
        showClockOutModal: true,
      })

      return true
    }

    return false
  }

  get displayMissingClockOutView(): boolean {
    const { openShift, policies } = this.props
    const canSeeAttendance = !!policies?.seeAttendance?.can

    return !!openShift && canSeeAttendance && this.exceedsEstimatedThreshold()
  }

  exceedsEstimatedThreshold = (shiftDuration = this.openShiftDuration || 0): boolean => {
    const { attendanceSetting, overtimeRequest } = this.props

    const missingClockOutEnabled = EmployeesSettingsUtils.missingClockOutEnabled(attendanceSetting)
    const forgotClockOutToleranceMinutes = attendanceSetting?.forgotClockOutToleranceMinutes

    return (
      missingClockOutEnabled &&
      this.exceedsWithForgotClockOutDuration(
        shiftDuration,
        forgotClockOutToleranceMinutes,
        AttendanceOvertimeUtils.minutes(overtimeRequest?.hoursAmountInCents || 0)
      )
    )
  }

  get isSameDayAsToday() {
    const { openShift } = this.props
    const todayDate = moment()
    const date = OpenShiftUtils.date(openShift) ?? todayDate

    return todayDate.startOf('day').isSame(date.clone().startOf('day'))
  }

  exceedsWithForgotClockOutDuration(
    duration: number,
    clockOutToleranceMinutes: number,
    overtimeRequestMinutes: number
  ): boolean {
    const trackedMinutes = this.trackedMinutesToday
    const estimatedMinutes = this.props.estimatedTime.minutes
    const threshold = estimatedMinutes + clockOutToleranceMinutes + overtimeRequestMinutes

    return trackedMinutes + duration > threshold
  }

  restTimeAlertInconsistencies = () => {
    const { breakDurationRules, todayShifts } = this.props

    if (isBreakSetting(breakDurationRules[0]) && breakDurationRules.length > 0) {
      return validateBreakDurationRules(todayShifts, breakDurationRules[0])
    }

    return []
  }

  onCheckToggle = (
    action: ActionType,
    locationType?: string,
    attendanceBreak?: AttendanceBreakConfigurations | undefined,
    projectData?: ProjectClockInData
  ) => {
    const { openShift, breakSetting } = this.props

    if (action === ActionTypes.CLOCK_OUT_ACTION) {
      return this.checkClockOutToggle({})
    }

    if (action === ActionTypes.BREAK_END_ACTION) {
      if (OpenShiftUtils.isAutomaticBreak(openShift)) {
        return this.setState({
          showFinishAutoBreakModal: true,
        })
      }
    }

    this.onToggle({
      action,
      estimatedClockOutTime: moment(),
      locationType,
      attendanceBreak,
      projectData,
    })
  }

  eventName = (action: ActionType) => {
    const { timeTracking } = trackingEvents

    switch (action) {
      case ActionTypes.CLOCK_IN_ACTION:
        return timeTracking.startClockIn
      case ActionTypes.CLOCK_OUT_ACTION:
        return timeTracking.startClockOut
      case ActionTypes.FORGOT_CLOCK_OUT_ACTION:
        return timeTracking.editClockOut
      case ActionTypes.BREAK_START_ACTION:
        return timeTracking.startBreak
      case ActionTypes.BREAK_END_ACTION:
        return timeTracking.startBackToWork
      default:
        return ''
    }
  }

  trackEvent = (
    action: ActionType,
    from: string,
    locationType?: string,
    attendanceBreak?: AttendanceBreakConfigurations | undefined,
    projectData?: ProjectClockInData
  ) => {
    const pathname = window.location.href.includes('attendance') ? 'attendance' : 'dashboard'
    const { attendanceSetting } = this.props
    const { enableClockInLocation } = attendanceSetting || {}

    let trackParams: TrackingProperties = {
      page_path: pathname,
      from: from,
      location_type: locationType,
      wfh_enabled: enableClockInLocation,
    }

    if (hasFeature('time_tracking_paid_breaks') && !!attendanceBreak) {
      trackParams = {
        ...trackParams,
        break_type: attendanceBreak.paid ? 'paid' : 'unpaid',
        break_id: attendanceBreak.timeSettingsBreakConfigurationId,
      }
    }

    trackParams = {
      ...trackParams,
      projectChosen: !!projectData?.projectWorkerId,
      subProjectChosen: !!projectData?.subprojectId,
      newWidget: true,
      hasProjectsEnabled: this.props.hasProjectEnabled,
    }

    if (action == ActionTypes.CLOCK_IN_ACTION) {
      trackParams = {
        ...trackParams,
        resolutionWidth: document.body.clientWidth,
        resolutionHeight: document.body.clientHeight,
      }
    }

    track(this.eventName(action), trackParams)
  }

  onToggle = async ({
    action,
    estimatedClockOutTime = moment(),
    from = ActionFrom.TIME_TRACKING_WIDGET,
    locationType,
    attendanceBreak,
    projectData,
  }: ToggleProps) => {
    const { setLoading, openShift, setToday } = this.props
    setLoading(true)

    const { projectWorkerId, subprojectId } = projectData || {}

    let data: ShiftToggleProps = {
      now: estimatedClockOutTime.format(),
      date: openShift?.date ?? dateToString(estimatedClockOutTime, 'backend'),
      source: 'desktop',
      locationType: locationType,
      projectWorkerId: projectWorkerId,
      subprojectId: subprojectId,
    }

    if (hasFeature('time_tracking_paid_breaks') && !!attendanceBreak) {
      data = {
        ...data,
        timeSettingsBreakConfigurationId: Number(attendanceBreak?.timeSettingsBreakConfigurationId),
      }
    }

    this.trackEvent(action, from, locationType, attendanceBreak, projectData)

    const unhandledResult = await this.shiftAction(action, data)

    let errorMessage = null
    if (unhandledResult) {
      handleResult(unhandledResult, (errrsHash) => (errorMessage = errrsHash.messages))
    }
    if (errorMessage) {
      app.setFlash({
        type: 'wrong',
        message: errorMessage,
      })

      setToday(null)
    } else {
      setToday(dateToString(moment().toDate(), 'backend'))
    }

    dispatch({
      type: EventTypes.ATTENDANCE_SHIFT_TOGGLED,
      data: { action },
    })
    setLoading(false)
    if (action === ActionTypes.CLOCK_OUT_ACTION) {
      this.setState({
        showEmployeePulseQuestionModal: true,
      })
    }
  }

  shiftAction = async (
    action: ActionType,
    data: ShiftToggleProps
  ): Promise<UnhandledResult<Result<unknown>> | void> => {
    const { shiftClockIn, shiftClockOut, shiftForgotClockOut, shiftBreakStart, shiftBreakEnd } =
      this.props.shiftActions

    const locationType = AttendanceShiftLocationTypeEnumValues.find(
      (location) => location === data.locationType
    )

    const actionMap = {
      [ActionTypes.CLOCK_IN_ACTION]: () =>
        shiftClockIn({
          ...data,
          locationType: locationType,
        }),
      [ActionTypes.CLOCK_OUT_ACTION]: () =>
        shiftClockOut({
          ...data,
          endOn: dateToString(moment(), 'backend'),
          startOn: data.date,
        }),
      [ActionTypes.FORGOT_CLOCK_OUT_ACTION]: () => shiftForgotClockOut(data),
      [ActionTypes.BREAK_START_ACTION]: () =>
        shiftBreakStart({
          ...data,
          timeSettingsBreakConfigurationId: data.timeSettingsBreakConfigurationId,
          endOn: dateToString(moment(), 'backend'),
          startOn: data.date,
        }),
      [ActionTypes.BREAK_END_ACTION]: () =>
        shiftBreakEnd({
          ...data,
          endOn: dateToString(moment(), 'backend'),
          startOn: data.date,
          projectWorkerId: data.projectWorkerId,
          subprojectId: data.subprojectId,
        }),
    }

    return actionMap[action]?.() || Promise.resolve()
  }

  closeClockOutModals = () => {
    this.setState({
      showStillBreakModal: false,
      showClockOutModal: false,
    })
  }

  closeMinBreakModal = () => {
    this.setState({
      showMinBreakModal: false,
    })
  }

  closeFinishAutoBreakModal = () => {
    this.setState({
      showFinishAutoBreakModal: false,
    })
  }

  confirmClockOut = (
    estimatedClockOutTime: moment.Moment,
    from: string,
    forgotClockOut: boolean
  ) => {
    this.closeClockOutModals()

    const action = forgotClockOut
      ? ActionTypes.FORGOT_CLOCK_OUT_ACTION
      : ActionTypes.CLOCK_OUT_ACTION

    this.onToggle({ action, estimatedClockOutTime, from })
  }

  confirmEndBreak = async () => {
    this.closeMinBreakModal()
    this.closeFinishAutoBreakModal()
    this.onToggle({
      action: ActionTypes.BREAK_END_ACTION,
      locationType: this.lastShiftLocationType,
      projectData: this.projectClockInData(),
    })
  }

  redirectTimeSheet = () => {
    this.closeClockOutModals()
    this.props.navigate(this.props.employeeTimeSheetUrl)
  }

  render() {
    const {
      showClockOutModal,
      showMinBreakModal,
      showFinishAutoBreakModal,
      showEmployeePulseQuestionModal,
    } = this.state

    const {
      attendanceSetting,
      today,
      children,
      openShift,
      lastWorkingShift,
      loading,
      breakSetting,
      todayShifts,
      estimatedTime,
      breaksEnabled,
    } = this.props
    const enableClockInLocation = !!attendanceSetting?.enableClockInLocation
    const clockedIn = !!openShift && openShift.workable && !this.displayMissingClockOutView
    const breakStarted = !!openShift && !openShift.workable
    const hasAutomaticBreaks = breakSetting?.automaticBreaks ?? false
    const hasRestTime = !!breakSetting?.manualBreaks && !!breakSetting?.restTime
    const lastShiftLocationType = this.lastShiftLocationType
    return (
      <>
        {showClockOutModal && !!openShift && (
          <ClockOutReminderModal
            workedDurationToday={this.trackedMinutesTodayWithOpenShift}
            maxMinutesPerDay={estimatedTime.minutes}
            onClose={this.closeClockOutModals}
            onConfirm={this.checkClockOutToggle}
          />
        )}
        {showEmployeePulseQuestionModal && (
          <EmployeePulseQuestion
            estimatedTime={estimatedTime.minutes}
            workedTime={this.trackedMinutesToday}
            onClose={() => this.setState({ showEmployeePulseQuestionModal: false })}
          />
        )}
        {showFinishAutoBreakModal && !!openShift && (
          <FinishAutoBreakModal
            onClose={this.closeFinishAutoBreakModal}
            onConfirm={this.confirmEndBreak}
          />
        )}
        {children({
          today,
          loading,
          onToggle: this.onCheckToggle,
          clockedIn,
          breakStarted,
          projectClockInData: this.projectClockInData(),
          hasAutomaticBreaks,
          hasRestTime,
          lastShiftLocationType,
          openShift,
          lastWorkingShift,
          enableClockInLocation,
          openShiftDuration: this.openShiftDuration,
          breakType: this.breakType,
          breakConfigurations: this.props.breakConfigurations,
          breakTypes: this.props.breakTypes,
          canSeeOwnedAttendance: this.props.canSeeOwnedAttendance,
          trackedDurationToday: this.trackedMinutesTodayWithOpenShift,
          employeeMissingClockOut: this.displayMissingClockOutView,
          exceedsEstimatedThreshold: this.exceedsEstimatedThreshold,
          restTimeAlertInconsistencies: this.restTimeAlertInconsistencies(),
          todayShifts,
          maxMinutesPerDay: estimatedTime.minutes,
          maxRegularMinutes: estimatedTime.regularMinutes,
          breaksEnabled,
          hasProjectEnabled: this.props.hasProjectEnabled,
          desktopClockIn: this.props.desktopClockIn,
          mobileClockIn: this.props.mobileClockIn,
          breakSetting,
        })}
      </>
    )
  }
}

const ClockInCheckerFC = (props: Props & React.ConsumerProps<RenderProps>) => {
  const { navigation } = useNavigation()
  const { featureAvailability: hasProjectsEnabled } = useAttendanceFeatures(
    Features.projects_management
  )

  return (
    <ClockInChecker
      {...props}
      employeeTimeSheetUrl={navigation.clockIn.fullPath()}
      hasProjectEnabled={hasProjectsEnabled}
    />
  )
}

export default observer(withHooks(ClockInCheckerFC))
